import { parsePhoneInput } from '../lib/format.ts';
import { isEmergencyInput } from './destination.ts';

export interface CallSessionLink {
  attemptId: string;
  candidateId: number;
  controllerId?: string;
}

export type CallLink =
  | { ok: true; e164: string; session?: CallSessionLink }
  | { ok: false; message: string };
const invalid = (): CallLink => ({ ok: false, message: 'This call link is invalid. Use a phone number without extensions or post-dial commands.' });
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CANDIDATE_ID_RE = /^[1-9][0-9]{0,14}$/;

/** Decode each query layer once. URLSearchParams alone silently repairs broken escapes/UTF-8. */
function parameters(query: string): Map<string, string> | null {
  if (!query.startsWith('?') || query.length <= 1) return null;
  const pairs = query.slice(1).split('&');
  if (pairs.length < 1 || pairs.length > 4) return null;
  const decode = (s: string) => decodeURIComponent(s.replace(/\+/g, ' '));
  const out = new Map<string, string>();
  for (const pair of pairs) {
    if (!pair || !pair.includes('=')) return null;
    const at = pair.indexOf('=');
    try {
      const key = decode(pair.slice(0, at));
      const value = decode(pair.slice(at + 1));
      if (!key || out.has(key)) return null;
      out.set(key, value);
    } catch {
      return null;
    }
  }
  return out;
}

function destination(value: string): CallLink {
  // A narrow phone alphabet prevents the phone library from discarding an extension or prose.
  if (value !== value.trim() || !/^(?:\+?[0-9]|\([0-9])[0-9 ()\.\-]*[0-9]$/.test(value) || value.length > 40) return invalid();
  if ((value.match(/\(/g)?.length ?? 0) !== (value.match(/\)/g)?.length ?? 0) || /\([^)]*\(/.test(value) || /\)[^(]*\)/.test(value)) return invalid();
  if (isEmergencyInput(value)) return { ok: false, message: 'Emergency and test numbers cannot be called from a link. Use a mobile or desk phone for emergencies.' };
  const parsed = parsePhoneInput(value); // Existing, explicit US national default.
  return parsed.ok ? { ok: true, e164: parsed.e164 } : invalid();
}

/** Accept only the pinned HTTPS receiving route, or the exact custom-protocol wire format. */
export function parseCallLink(raw: string, origin = 'https://sidet.one'): CallLink {
  if (raw.length > 2048 || /[\u0000-\u0020\u007f\\#]/.test(raw)) return invalid();
  try {
    const url = new URL(raw);
    if (url.hash || url.username || url.password) return invalid();
    const protocol = url.protocol === 'web+sidetone:';
    if (protocol) {
      if (!raw.startsWith('web+sidetone://call?') || url.host !== 'call' || url.pathname !== '' || url.port) return invalid();
    } else {
      if (url.origin !== origin || !raw.startsWith(`${origin}/call?`) || url.pathname !== '/call') return invalid();
    }
    const params = parameters(url.search);
    if (!params) return invalid();
    if (params.size === 1) {
      if (params.has('number')) return destination(params.get('number')!);
      const uri = params.get('uri');
      if (!protocol && uri && uri.startsWith('web+sidetone://')) return parseCallLink(uri, origin);
      return invalid();
    }
    if (!protocol && (params.size === 3 || params.size === 4)) {
      const numberVal = params.get('number');
      const attemptId = params.get('attempt_id');
      const candidateRaw = params.get('candidate_id');
      const controllerId = params.get('controller_id');
      if (!numberVal || !attemptId || !candidateRaw) return invalid();
      if (params.size === 4 && !controllerId) return invalid();
      if (!UUID_RE.test(attemptId) || !CANDIDATE_ID_RE.test(candidateRaw)) return invalid();
      if (controllerId !== undefined && !UUID_RE.test(controllerId)) return invalid();
      const candidateId = Number(candidateRaw);
      if (!Number.isSafeInteger(candidateId) || candidateId <= 0) return invalid();
      const dest = destination(numberVal);
      if (!dest.ok) return dest;
      return {
        ok: true,
        e164: dest.e164,
        session: {
          attemptId: attemptId.toLowerCase(),
          candidateId,
          ...(controllerId ? { controllerId: controllerId.toLowerCase() } : {}),
        },
      };
    }
    return invalid();
  } catch { return invalid(); }
}
